
import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { 
  MessageSquare, 
  ArrowLeft, 
  ThumbsUp, 
  Bookmark, 
  Share,
  Heart,
  Send,
  User,
  Eye
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

/**
 * Interface for Community Post objects
 */
interface CommunityPost {
  id: number;
  title: string;
  content: string;
  author: string;
  likes: number;
  comments: number;
  timePosted: string;
  tags: string[];
  authorAvatar: string;
  forumId?: number;
  pinned?: boolean;
  views?: number;
  liked?: boolean;
  bookmarked?: boolean;
  commentsList?: {
    id: number;
    author: string;
    content: string;
    timePosted: string;
    authorAvatar: string;
    likes: number;
  }[];
}

/**
 * CommunityPostDetail component displays a full page view of a single community post
 * and allows users to view and add comments.
 * 
 * @returns JSX.Element - The rendered post detail page
 */
const CommunityPostDetail = () => {
  // Get toast functionality from the toast hook
  const { toast } = useToast();
  const navigate = useNavigate();
  const { postId } = useParams<{ postId: string }>();
  
  // State for the selected post
  const [post, setPost] = useState<CommunityPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState("");
  
  // Ref to scroll to bottom of comments
  const commentsEndRef = useRef<HTMLDivElement>(null);

  // Forum data for context
  const forumNames: Record<number, string> = {
    1: "General Discussion",
    2: "Strategy Exchange",
    3: "Investing News",
    4: "Beginners Corner"
  };

  // Mock data for posts - in a real app this would come from an API
  const mockPosts: CommunityPost[] = [
    {
      id: 1,
      title: "Tips for identifying growth stocks?",
      content: "Hey everyone! I've been trying to improve my portfolio by identifying promising growth stocks. What strategies do you all use to spot potential winners before they take off? Are there specific metrics you look at or resources you recommend?",
      author: "Kwesi Bimpong",
      likes: 32,
      comments: 18,
      timePosted: "2 hours ago",
      tags: ["Investing", "Stocks"],
      authorAvatar: "/placeholder.svg",
      views: 128,
      forumId: 3, // Investing News
      liked: false,
      bookmarked: false,
      commentsList: [
        {
          id: 1,
          author: "Tes Ghezae",
          content: "I always check the company's revenue growth rate over the past 3 years. Anything consistently above 20% gets my attention!",
          timePosted: "1 hour ago",
          authorAvatar: "/placeholder.svg",
          likes: 8
        },
        {
          id: 2,
          author: "Jordan Smith",
          content: "Don't forget to look at the management team! I've found that innovative leaders often drive substantial growth.",
          timePosted: "45 minutes ago",
          authorAvatar: "/placeholder.svg",
          likes: 5
        }
      ]
    },
    {
      id: 2,
      title: "How did you start your first investment?",
      content: "I'm looking to make my first investment but feeling overwhelmed by all the options. How did you all get started? What was your first investment and what would you recommend for a complete beginner?",
      author: "Tes Ghezae",
      likes: 27,
      comments: 42,
      timePosted: "6 hours ago",
      tags: ["Beginners", "Getting Started"],
      authorAvatar: "/placeholder.svg",
      views: 245,
      forumId: 4, // Beginners Corner
      liked: false,
      bookmarked: false,
      commentsList: [
        {
          id: 1,
          author: "Kwesi Bimpong",
          content: "I started with a low-cost index fund that tracks the S&P 500. It gave me broad market exposure with minimal fees.",
          timePosted: "5 hours ago",
          authorAvatar: "/placeholder.svg",
          likes: 14
        },
        {
          id: 2,
          author: "Sarah Williams",
          content: "My first investment was through my employer's 401(k). It was a great way to start because of the employer match - basically free money!",
          timePosted: "3 hours ago",
          authorAvatar: "/placeholder.svg",
          likes: 10
        },
        {
          id: 3,
          author: "David Chen",
          content: "I'd recommend starting with a robo-advisor like Betterment or Wealthfront. They handle all the allocation decisions for you based on your risk tolerance.",
          timePosted: "2 hours ago",
          authorAvatar: "/placeholder.svg",
          likes: 7
        }
      ]
    },
    // ... more mock posts
  ];

  // Load post data
  useEffect(() => {
    // In a real app, this would be an API call
    // For now, we'll use mock data and simulate a network request
    setLoading(true);
    
    setTimeout(() => {
      if (postId) {
        const numericPostId = parseInt(postId);
        const foundPost = mockPosts.find(p => p.id === numericPostId);
        
        if (foundPost) {
          // Increment view count
          setPost({
            ...foundPost,
            views: (foundPost.views || 0) + 1
          });
        } else {
          toast({
            title: "Post not found",
            description: "The requested post could not be found.",
            variant: "destructive"
          });
          navigate("/community");
        }
      }
      setLoading(false);
    }, 300); // Simulate network delay
  }, [postId, navigate, toast]);

  /**
   * Scrolls to the bottom of the comments section
   */
  const scrollToBottom = () => {
    commentsEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  /**
   * Handler for liking a post
   */
  const handleLikePost = () => {
    if (!post) return;
    
    const newLikedState = !post.liked;
    setPost({
      ...post,
      liked: newLikedState,
      likes: newLikedState ? post.likes + 1 : post.likes - 1
    });
  };

  /**
   * Handler for bookmarking a post
   */
  const handleBookmarkPost = () => {
    if (!post) return;
    
    const newBookmarkedState = !post.bookmarked;
    setPost({
      ...post,
      bookmarked: newBookmarkedState
    });
    
    toast({
      title: newBookmarkedState ? "Post saved" : "Post unsaved",
      description: newBookmarkedState 
        ? "This post has been added to your bookmarks."
        : "This post has been removed from your bookmarks."
    });
  };

  /**
   * Handler for adding a comment to a post
   */
  const handleAddComment = () => {
    if (!post) return;
    
    if (!newComment.trim()) {
      toast({
        title: "Empty comment",
        description: "Please enter a comment before submitting.",
        variant: "destructive"
      });
      return;
    }

    const newCommentObj = {
      id: (post.commentsList?.length || 0) + 1,
      author: "You", // Assuming the current user
      content: newComment,
      timePosted: "Just now",
      authorAvatar: "/placeholder.svg",
      likes: 0
    };

    setPost({
      ...post,
      comments: post.comments + 1,
      commentsList: [...(post.commentsList || []), newCommentObj]
    });

    // Reset comment input
    setNewComment("");

    // Show toast notification
    toast({
      title: "Comment added",
      description: "Your comment has been posted successfully.",
    });

    // Scroll to the bottom of comments after a short delay
    setTimeout(() => {
      scrollToBottom();
    }, 100);
  };

  /**
   * Handler for liking a comment
   * @param commentId - ID of the comment to like
   */
  const handleLikeComment = (commentId: number) => {
    if (!post || !post.commentsList) return;
    
    setPost({
      ...post,
      commentsList: post.commentsList.map(comment => {
        if (comment.id === commentId) {
          return {
            ...comment,
            likes: comment.likes + 1
          };
        }
        return comment;
      })
    });
  };

  /**
   * Handler for going back to the community page
   */
  const handleBack = () => {
    navigate('/community');
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-8rem)]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Post not found state
  if (!post) {
    return (
      <div className="min-h-[calc(100vh-8rem)] flex flex-col items-center justify-center">
        <MessageSquare className="w-16 h-16 text-gray-300 mb-4" />
        <h2 className="text-2xl font-bold mb-2">Post Not Found</h2>
        <p className="text-gray-500 mb-6">The post you're looking for doesn't exist or has been removed.</p>
        <Button onClick={handleBack}>
          <ArrowLeft className="mr-2 h-4 w-4" /> 
          Back to Community
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-8rem)] space-y-6 pb-16">
      {/* Back button and navigation */}
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={handleBack} className="gap-1">
          <ArrowLeft className="h-4 w-4" />
          Back to Community
        </Button>
        
        {post.forumId && (
          <div className="text-sm text-gray-500">
            Posted in: <span className="text-primary font-medium">{forumNames[post.forumId]}</span>
          </div>
        )}
      </div>
      
      {/* Post header and content */}
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">{post.title}</h1>
          
          <div className="flex justify-between items-center mb-2">
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={post.authorAvatar} alt={post.author} />
                <AvatarFallback>{post.author.slice(0, 2)}</AvatarFallback>
              </Avatar>
              <div>
                <p className="font-medium">{post.author}</p>
                <p className="text-sm text-gray-500">{post.timePosted}</p>
              </div>
            </div>
            
            <div className="flex items-center text-sm text-gray-500 gap-2">
              <span className="flex items-center gap-1">
                <Eye className="h-4 w-4" /> {post.views} views
              </span>
            </div>
          </div>
          
          <div className="flex gap-2 mt-2 mb-4 flex-wrap">
            {post.tags.map((tag, i) => (
              <Badge key={i} variant="secondary" className="bg-secondary/10 text-secondary">
                {tag}
              </Badge>
            ))}
          </div>
        </div>
        
        {/* Post content */}
        <Card>
          <CardContent className="pt-6 pb-4">
            <p className="text-gray-700 whitespace-pre-line">{post.content}</p>
          </CardContent>
        </Card>
        
        {/* Post actions */}
        <div className="flex gap-4 py-2">
          <Button 
            variant="outline" 
            size="sm"
            className={`gap-2 ${post.liked ? 'text-primary border-primary bg-primary/5' : ''}`}
            onClick={handleLikePost}
          >
            <ThumbsUp className="h-4 w-4" /> 
            <span>{post.likes} {post.likes === 1 ? 'Like' : 'Likes'}</span>
          </Button>
          
          <Button 
            variant="outline" 
            size="sm"
            className={`gap-2 ${post.bookmarked ? 'text-primary border-primary bg-primary/5' : ''}`}
            onClick={handleBookmarkPost}
          >
            <Bookmark className="h-4 w-4" />
            <span>{post.bookmarked ? 'Saved' : 'Save'}</span>
          </Button>
          
          <Button variant="outline" size="sm" className="gap-2">
            <Share className="h-4 w-4" />
            <span>Share</span>
          </Button>
        </div>
        
        <Separator className="my-6" />
        
        {/* Comments section */}
        <div className="space-y-6">
          <h2 className="text-2xl font-semibold">
            Comments ({post.comments})
          </h2>
          
          <div className="space-y-4">
            {post.commentsList && post.commentsList.length > 0 ? (
              post.commentsList.map((comment) => (
                <Card key={comment.id} className="overflow-hidden">
                  <CardContent className="p-4 space-y-2">
                    <div className="flex justify-between">
                      <div className="flex items-center gap-2">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={comment.authorAvatar} alt={comment.author} />
                          <AvatarFallback>{comment.author.slice(0, 2)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{comment.author}</p>
                          <p className="text-xs text-gray-500">{comment.timePosted}</p>
                        </div>
                      </div>
                      
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-8 w-8 p-0" 
                        onClick={() => handleLikeComment(comment.id)}
                      >
                        <Heart className="h-4 w-4" />
                        {comment.likes > 0 && <span className="text-xs ml-1">{comment.likes}</span>}
                      </Button>
                    </div>
                    
                    <p className="text-gray-700 mt-1">{comment.content}</p>
                  </CardContent>
                </Card>
              ))
            ) : (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-lg">No comments yet</p>
                <p className="text-gray-400">Be the first to share your thoughts!</p>
              </div>
            )}
            {/* Invisible element to scroll to */}
            <div ref={commentsEndRef} />
          </div>
          
          {/* Add comment section */}
          <div className="pt-4">
            <h3 className="text-lg font-medium mb-4">Add a Comment</h3>
            <div className="flex gap-3 items-start">
              <Avatar className="h-10 w-10 mt-1">
                <AvatarImage src="/placeholder.svg" alt="You" />
                <AvatarFallback><User className="h-5 w-5" /></AvatarFallback>
              </Avatar>
              <div className="flex-1 space-y-3">
                <Input
                  placeholder="Share your thoughts..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  className="flex-1"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleAddComment();
                    }
                  }}
                />
                <Button 
                  className="gap-2"
                  onClick={handleAddComment}
                >
                  <Send className="h-4 w-4" />
                  Post Comment
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunityPostDetail;
